import { FitAddon } from "@xterm/addon-fit";
import { Terminal as XTerm } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@/lib/media.ts";
import { cn } from "@/lib/cn.ts";
import { wsUrl } from "@/api/chats.ts";

/**
 * xterm.js attached to /ws/terminal. Binary frames = PTY bytes; resize is sent as JSON text.
 * With `session`, the server keeps the shell alive across network drops and the client
 * reconnects with backoff, replaying buffered output.
 */
export function Terminal({ target, session, onExit, className }: { target: string; session?: string; onExit?: (reason: string) => void; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // touch screens: an extra key row (phone keyboards have no Esc, Tab, Ctrl or arrows)
  const touch = useMediaQuery("(pointer: coarse)");
  const sendRef = useRef<(d: string) => void>(() => {});
  const ctrlRef = useRef(false);
  const [ctrl, setCtrl] = useState(false);
  const focusRef = useRef<() => void>(() => {});
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const dark = document.documentElement.dataset.theme === "dark";
    const term = new XTerm({
      cursorBlink: true,
      fontFamily: "ui-monospace, Menlo, monospace",
      fontSize: 13,
      convertEol: false,
      theme: dark ? { background: "#15171c", foreground: "#e6e8ee" } : { background: "#ffffff", foreground: "#1f2430", cursor: "#1f2430" },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(el);
    fit.fit();
    const enc = new TextEncoder();
    let ws: WebSocket | null = null;
    let disposed = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (disposed) return;
      const q = new URLSearchParams({ target, cols: String(term.cols), rows: String(term.rows) });
      if (session) q.set("session", session);
      const sock = new WebSocket(wsUrl(`/ws/terminal?${q}`));
      sock.binaryType = "arraybuffer";
      ws = sock;
      sock.onopen = () => {
        if (attempts > 0) term.write("\r\n\x1b[2m[reconnected]\x1b[0m\r\n");
        attempts = 0;
      };
      sock.onmessage = (e) => {
        if (typeof e.data === "string") term.write(e.data);
        else term.write(new Uint8Array(e.data as ArrayBuffer));
      };
      sock.onclose = (e) => {
        if (disposed) return;
        // Server-side end (exit, auth failure) → stop. Abnormal close with a session → retry.
        const final = e.code === 1000 || e.code === 4401 || e.code === 4400 || !session;
        if (final) {
          term.write(`\r\n\x1b[2m[${e.reason || "closed"}]\x1b[0m\r\n`);
          onExit?.(e.reason);
          return;
        }
        attempts += 1;
        const delay = Math.min(15_000, 500 * 2 ** Math.min(attempts, 5));
        term.write(`\r\n\x1b[2m[connection lost, retrying in ${Math.round(delay / 1000)}s]\x1b[0m\r\n`);
        timer = setTimeout(connect, delay);
      };
    };
    connect();
    const online = () => {
      if (ws && ws.readyState !== WebSocket.OPEN && timer) {
        clearTimeout(timer);
        connect();
      }
    };
    window.addEventListener("online", online);

    const send = (d: string) => { if (ws?.readyState === WebSocket.OPEN) ws.send(enc.encode(d)); };
    sendRef.current = send;
    focusRef.current = () => term.focus();
    const dataSub = term.onData((d) => {
      // sticky Ctrl from the key bar: the next letter becomes its control code (Ctrl+C = \x03)
      if (ctrlRef.current && d.length === 1 && /[a-z@\[\]\\^_]/i.test(d)) {
        ctrlRef.current = false;
        setCtrl(false);
        send(String.fromCharCode(d.toUpperCase().charCodeAt(0) & 0x1f));
        return;
      }
      send(d);
    });
    const resizeSub = term.onResize(({ cols, rows }) => ws?.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "resize", cols, rows })));
    const ro = new ResizeObserver(() => fit.fit());
    ro.observe(el);
    term.focus();
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", online);
      ro.disconnect();
      dataSub.dispose();
      resizeSub.dispose();
      ws?.close();
      term.dispose();
    };
  }, [target, session, onExit, touch]); // the host element changes when the key bar appears
  const keys: [string, string][] = [["Esc", "\x1b"], ["Tab", "\t"], ["↑", "\x1b[A"], ["↓", "\x1b[B"], ["←", "\x1b[D"], ["→", "\x1b[C"], ["|", "|"], ["~", "~"], ["/", "/"], ["-", "-"]];
  const press = (seq: string) => { sendRef.current(seq); focusRef.current(); };
  if (!touch) return <div ref={ref} dir="ltr" className={className ?? "h-[420px] w-full overflow-hidden rounded-md border border-border bg-[#15171c] p-1"} />;
  return (
    <div className={cn("flex flex-col", className ?? "h-[420px] w-full overflow-hidden rounded-md border border-border bg-[#15171c]")}>
      <div ref={ref} dir="ltr" className="min-h-0 flex-1 p-1" />
      <div dir="ltr" className="no-scrollbar flex shrink-0 gap-1 overflow-x-auto border-t border-white/10 bg-[#101216] px-1 py-1 pb-safe">
        <button type="button" onPointerDown={(e) => e.preventDefault()} onClick={() => { ctrlRef.current = !ctrlRef.current; setCtrl(ctrlRef.current); focusRef.current(); }} className={cn("tap shrink-0 rounded-md px-3 font-mono text-xs", ctrl ? "bg-accent text-accent-fg" : "bg-white/10 text-white/80")}>Ctrl</button>
        {keys.map(([label, seq]) => (
          <button key={label} type="button" onPointerDown={(e) => e.preventDefault()} onClick={() => press(seq)} className="tap shrink-0 rounded-md bg-white/10 px-3 font-mono text-xs text-white/80 active:bg-white/20">{label}</button>
        ))}
      </div>
    </div>
  );
}
